import { Component, ElementRef, ViewChild, Output, EventEmitter } from '@angular/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { FormsModule } from '@angular/forms';
import { ChatInputComponent } from './chat-input/chat-input.component';
import { ComponentData, MessageRendererComponent } from './components/message-renderer/message-renderer.component';
import { catchError, of } from 'rxjs';
import { ApiService, QueryResponse } from '../app/services/api.service';
import { Msg, StreamEvent } from '../app/types';


@Component({
  selector: 'app-chat',
  standalone: true,
  imports: [
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatIconModule,
    FormsModule,
    ChatInputComponent,
    MessageRendererComponent
  ],
  templateUrl: './chat.component.html',
  styleUrl: './chat.component.scss'
})
export class ChatComponent {
  @ViewChild('scrollBox') scrollBox!: ElementRef<HTMLDivElement>;
  @Output() debugIdChanged = new EventEmitter<string | null>();
  
  user_query: string | null = null;
  messages: Msg[] = [];
  loading = false;
  streamBubbleVisible = false;
  streamBubbleMessage = 'Thinking...';
  // Backend conversation state; never rendered in UI.
  private conversationId: string | null = null;

  constructor(
    protected api: ApiService
  ) {
    
  }

  resetConversation() {
    this.messages = [];
    this.loading = false;
    this.streamBubbleVisible = false;
    this.conversationId = null;
    this.debugIdChanged.emit(null);
  }

  loadConversation(conversationId: string): void {
    this.loading = true;
    this.streamBubbleVisible = false;
    this.streamBubbleMessage = 'Thinking...';
    this.conversationId = conversationId;
    this.debugIdChanged.emit(null);
    this.messages = [];

    this.api.getMarineSurveyMessages(conversationId).pipe(
      catchError((e) => {
        console.error('There was an error loading conversation', e);
        return of({ messages: [] });
      }),
    ).subscribe((response: { messages?: Array<{ role: string; content: string }> }) => {
      const history = response.messages ?? [];
      this.messages = history.map((message) => {
        if (message.role === 'assistant') {
          return {
            role: 'assistant',
            content: JSON.parse(message.content)['doc'],
          };
        }
        return {
          role: message.role,
          content: message.content,
        };
      });
      this.loading = false;
      setTimeout(() => this.scrollToBottom(), 0);
    });
  }

  scrollToBottom() {
    // Use requestAnimationFrame to ensure DOM has updated
    requestAnimationFrame(() => {
      const el = this.scrollBox.nativeElement;
      el.scrollTop = el.scrollHeight;
    });
  }

  onSend(message: string, includeUserBubble: boolean = true) {
    this.sendMessage(message, includeUserBubble);
  }

  onStreamSend(message: string, includeUserBubble: boolean = true): void {
    void this.sendMessageStream(message, includeUserBubble);
  }

  private async sendMessageStream(message: string, includeUserBubble: boolean): Promise<void> {
    if (includeUserBubble) {
      this.messages.push({ content: message, role: 'user' });
    }

    this.loading = true;
    this.streamBubbleVisible = true;
    this.streamBubbleMessage = 'Thinking...';
    setTimeout(() => this.scrollToBottom(), 0);

    let hasTerminalResponse = false;

    try {
      const streamResult = await this.api.stream(
        message,
        this.conversationId,
        (event: StreamEvent) => {
          if (event.type === 'session') {
            const nextConversationId = event.data?.conversation_id;
            if (typeof nextConversationId === 'string' && nextConversationId.trim()) {
              this.conversationId = nextConversationId;
            }
            return;
          }

          if (event.type === 'tool_start' || event.type === 'tool_end') {
            const nextMessage = event.data?.message;
            if (typeof nextMessage === 'string' && nextMessage.trim()) {
              this.streamBubbleMessage = nextMessage;
              setTimeout(() => this.scrollToBottom(), 0);
            }
            return;
          }

          if (event.type === 'response') {
            hasTerminalResponse = true;
            this.streamBubbleVisible = false;
            const responseMessage = event.data?.doc ?? [];
            this.messages.push({
              content: responseMessage,
              role: 'assistant',
            });
            this.loading = false;
            setTimeout(() => this.scrollToBottom(), 0);
            return;
          }

          if (event.type === 'error') {
            hasTerminalResponse = true;
            this.streamBubbleVisible = false;
            this.messages.push({
              content: event.data?.message ?? 'Streaming request failed.',
              role: 'error',
            });
            this.loading = false;
            setTimeout(() => this.scrollToBottom(), 0);
          }
        },
      );

      this.conversationId = streamResult.conversation_id;
      if (!hasTerminalResponse) {
        this.streamBubbleVisible = false;
        this.loading = false;
      }
    } catch (e: any) {
      this.streamBubbleVisible = false;
      this.loading = false;
      this.messages.push({
        content: e?.message ?? 'Streaming request failed.',
        role: 'error',
      });
      setTimeout(() => this.scrollToBottom(), 0);
    }
  }

  private sendMessage(message: string, includeUserBubble: boolean) {
    if (includeUserBubble) {
      this.messages.push({ content: message, role: 'user' });
      setTimeout(() => this.scrollToBottom(), 0);
    }

    this.loading = true;
    // Keep prompt-originated submissions pinned to the latest activity even
    // when no user bubble is added.
    if (!includeUserBubble) {
      setTimeout(() => this.scrollToBottom(), 0);
    }
    const request$ = this.api.query(message, this.conversationId);

    request$.pipe(
      catchError(
        (e) => {
          console.error('There was an error', e);
          return of({
            code: 500,
          });
        },
      ),
    ).subscribe(
      (response: any) => {
        if (!response.code) {
          const typed = response as QueryResponse;
          this.conversationId = typed.conversation_id ?? this.conversationId;
          this.debugIdChanged.emit(typed.debug_id ?? null);
          if (typed.debug_id) {
            this.api.refreshDebugCached(typed.debug_id).pipe(
              catchError((e) => {
                console.error('Error refreshing debug doc:', e);
                return of(null);
              }),
            ).subscribe();
          }

          const responseMessage = response?.message?.doc ?? [];
          this.messages.push({
            content: responseMessage,
            role: 'assistant',
          });

          setTimeout(() => this.scrollToBottom(), 0);
        } else {
          this.messages.push({
            content: response.message,
            role: 'error',
          });
          setTimeout(() => this.scrollToBottom(), 0);
        }
        this.loading = false;
      },
    );
  }

  private parseAssistantContent(content: string): ComponentData[] {
    try {
      const parsed = JSON.parse(content);
      if (parsed && typeof parsed === 'object' && Array.isArray(parsed.doc)) {
        return MessageRendererComponent.parseMessage(parsed.doc);
      }
      return MessageRendererComponent.parseMessage(parsed);
    } catch {
      return MessageRendererComponent.parseMessage(content);
    }
  }
}
